import { yupResolver } from '@hookform/resolvers/yup';
import { useContext } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import AuthSettingsItemHeader from '../../components/common/AuthSettingsItemHeader';
import { DashboardButton } from '../../components/common/Button';
import { InputWord } from '../../components/common/Input';
import { PasswordConstraint } from '../../components/common/PasswordConstraint';
import { ToastQueueRefContext } from '../../components/common/Toast';
import { updatePasswordSchema, updatePasswordType } from '../../schema/DashboardSchema';
import UsersService from '../../services/users.service';

const DashboardAuthenticationPasswordComponent = () => {
  const navigate = useNavigate();
  const usersService = new UsersService();
  const toastQueueRef = useContext(ToastQueueRefContext);
  const { logout } = useAuth();

  const updatePasswordMethods = useForm<updatePasswordType>({
    resolver: yupResolver(updatePasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      actualPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const { watch } = updatePasswordMethods;

  const updatePassword = async ({ actualPassword, confirmNewPassword }: updatePasswordType) => {
    await usersService
      .updatePassword({
        oldPassword: actualPassword,
        newPassword: confirmNewPassword,
      })
      .then(() =>
        toastQueueRef?.current?.addToast({
          type: 'SUCCESS',
          description: 'Mot de passe mis à jour',
          timer: 5000,
        }),
      );

    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <AuthSettingsItemHeader
        title='Modifier votre mot de passe'
        description='Pour votre sécurité, votre mot de passe doit être strictement personnel. Ne le divulguez à personne.'
      />
      <FormProvider {...updatePasswordMethods}>
        <form onSubmit={updatePasswordMethods.handleSubmit(updatePassword)}>
          <div className='w-1/2 max-md:w-full'>
            <Controller
              name='actualPassword'
              control={updatePasswordMethods.control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <InputWord
                    label='Mot de passe actuel'
                    type='password'
                    placeholder='Mot de passe'
                    value={value}
                    onChangeValue={onChange}
                    errorMessage={error?.message}
                    autoComplete='off'
                  />
                </>
              )}
            />
          </div>
          <div className='w-1/2 max-md:w-full'>
            <Controller
              name='newPassword'
              control={updatePasswordMethods.control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <InputWord
                    label='Nouveau mot de passe'
                    type='password'
                    placeholder='Mot de passe'
                    value={value}
                    onChangeValue={onChange}
                    errorMessage={error?.message}
                    autoComplete='off'
                  />
                </>
              )}
            />
          </div>
          <PasswordConstraint password={watch('newPassword')} />
          <div className='w-1/2 max-md:w-full'>
            <Controller
              name='confirmNewPassword'
              control={updatePasswordMethods.control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <InputWord
                    label='Confirmer le nouveau mot de passe'
                    type='password'
                    placeholder='Mot de passe'
                    value={value}
                    onChangeValue={onChange}
                    errorMessage={error?.message}
                    autoComplete='off'
                  />
                </>
              )}
            />
          </div>
          <div className='mt-6 flex justify-end'>
            <DashboardButton type='cancel' onClick={() => navigate('/dashboard/authentication')} />
            <DashboardButton type='confirm' />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default DashboardAuthenticationPasswordComponent;
